import { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import '../MCPServerSetting.css';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import McpServerApi from '@/common/constant/api/McpServerApi';
import OverFlowTooltipWithTabsMui from '@/component/common/OverFlowTooltipWithTabsMui';
import { renderSettingApiStatus, renderSettingMCPServer } from '../handler/MCPServerUIHandler';
import { createNewInitApiData, transformApiDataClient, handleUpdateMCPServerByAction, getMCPServerById } from '../handler/MCPServerLogicHandler';
import { toggleLocalMcpInOrg } from '@/service/mcpServerService';
import { LOCAL_MCP_LABEL_PREFIX } from '@/component/mcpCatalog/McpCatalogConstant';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { closeTabId, multiPanelAtom, mcpActivationChangedAtom } from '@/state/atom/panelAtom';
import { closePanelTabSelector } from '@/state/selector/panelSelector';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';

MCPServerList.propTypes = {
    panelType: PropTypes.string,
    listSettingApi: PropTypes.array,
    setListSettingApi: PropTypes.func,
    setEditApiData: PropTypes.func,
    setIsLoading: PropTypes.func,
    corporationDetail: PropTypes.object,
    projectInuseDetail: PropTypes.object,
    userDetail: PropTypes.object,
    props: PropTypes.object,
    localMcpList: PropTypes.array,
    setLocalMcpList: PropTypes.func
};

function MCPServerList({
    setIsLoading,
    panelType,
    listSettingApi,
    setListSettingApi,
    setEditApiData,
    corporationDetail,
    projectInuseDetail,
    userDetail,
    props,
    localMcpList = [],
    setLocalMcpList
}) {
    // Re-render when the translation dictionary loads/updates.
    useTranslationSubscription();

    const mcpServerListBody = useRef(null);
    const [isShowPopUpReload, setIsShowPopUpReload] = useState(false);
    
    const [closeTabIdState, setCloseTabIdState] = useRecoilState(closeTabId);
    const closePanelTab = useSetRecoilState(closePanelTabSelector);
    const [panelList, setPanelList] = useRecoilState(multiPanelAtom);
    const setMcpActivationChanged = useSetRecoilState(mcpActivationChangedAtom);

    const callToggleLocalMcp = (catId, newValue) => {
        setLocalMcpList(prev =>
            prev.map(item => item.catId === catId ? { ...item, isActive: newValue } : item)
        );
        setIsLoading(true);
        toggleLocalMcpInOrg(catId, newValue, corporationDetail.id, userDetail.id)
            .then(res => {
                if (res?.success) {
                    // MCP-003-F3 BR-11: update localMcpActivated for UserProfile to re-check eligibility
                    // On Org Level, only the changed mcp is OFF. Because org off -> prj off
                    if (newValue === false) {
                        setMcpActivationChanged(prev => ({
                            ...prev,
                            userLocalMcp: { ...(prev?.userLocalMcp || {}), [catId]: newValue },
                            projectLocalMcp: { ...(prev?.projectLocalMcp || {}), [catId]: newValue }
                        }));
                    }
                }
                if (!res?.success) {
                    setLocalMcpList(prev =>
                        prev.map(item => item.catId === catId ? { ...item, isActive: !newValue } : item)
                    );
                    if (res?.error === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
                        setIsShowPopUpReload(true);
                    }
                }
            })
            .catch(err => {
                console.log('callToggleLocalMcp err: ', err);
                setLocalMcpList(prev =>
                    prev.map(item => item.catId === catId ? { ...item, isActive: !newValue } : item)
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const showLocalMcpSection = () => {
        const limitValue = corporationDetail?.limitValue;
        const corpSetting = Utils.convertJSONDataToObject(corporationDetail?.corporationSetting);
        const isOnMcp = limitValue?.mcpServer === Constant.STATUS_SERVICE_PERMISSION.ON
            && corpSetting?.usingMCPServer === Constant.STATUS_SERVICE_PERMISSION.ON;

        return isOnMcp
    };

    const callActiveInOrg = (activeValue, mcpServerData) => {
        let mcpData = {
            id: mcpServerData.id,
            isActiveCorp: activeValue
        };

        handleUpdateSuccess(null, mcpServerData, activeValue)
        setIsLoading(true);
        handleUpdateMCPServerByAction(
            McpServerApi.MCP_SERVER_UPDATE_ACTION_TYPE.TOGGLE_ACTIVE_IN_ORG,
            mcpData,
            {
                corporationInuse: corporationDetail.id,
                projectInuse: projectInuseDetail.id,
                userId: userDetail.id,
                panelType
            }
        )
            .then(response => handleUpdateSuccess(response, mcpServerData, activeValue))
            .catch(err => {
                console.log('handleUpdateMCPServerByAction err: ', err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleUpdateSuccess = (response, mcpServerData, isActiveCorp) => {
        if (!Utils.isEmptyString(response?.error)) {
            if (response?.error === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
                setIsShowPopUpReload(true);
            }
        }
        setListSettingApi(prev =>
            prev.map(item => (item.id === mcpServerData?.id ? { ...item, isActiveCorp } : item))
        );
    };

    useEffect(() => {
        if (props.tabId === closeTabIdState) {
            let currentPanelInfo = Utils.getPanelInfo(props.panelId, panelList);
            if (!currentPanelInfo || Utils.isEmptyObject(currentPanelInfo)) return;
            closePanelTab({
                event: null,
                panelType: currentPanelInfo.type,
                panelId: props.panelId,
                tabId: props.tabId
            });
            setCloseTabIdState(0);
        }
    }, [closeTabIdState]);

    const handleOpenDetail = async apiData => {
        setIsLoading(true);
        getMCPServerById(apiData)
            .then(response => {
                const { mcpServerDetail, error } = response;
                if (!Utils.isEmptyString(error)) {
                    if (error === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
                        setIsShowPopUpReload(true);
                        return;
                    }
                }
                setEditApiData(transformApiDataClient(mcpServerDetail));
            })
            .catch(err => {
                console.log('callActiveInOrg err: ', err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <div style={{ height: '100%' }}>
            <div className="mcp-server-header mcp-server-line-space">
                <div className="mcp-server-header-inner">
                    <h3 className="mcp-server-setting-list">
                        {trI18n('MCP_SERVER_SETTING_LIST_ORG')}
                    </h3>
                    <button
                        className="add-more-mcp-server-btn"
                        onClick={() => setEditApiData(createNewInitApiData())}
                    >
                        <img
                            className="add-more-mcp-server-icon"
                            src="/assets/image/icon-plus-add.svg"
                            alt="icon-plus-add"
                        />
                    </button>
                </div>
            </div>
            <div className="mcp-server-body">
                {!Utils.isEmptyArray(listSettingApi) && <div className="mcp-server-body-line mcp-server-line-space mcp-server-body-line-org">
                    <div className="">
                        <h4>{trI18n('MCP_SERVER_SETTING_LABEL_ORG')}</h4>
                    </div>
                    <div className="mcp-server-last-title">
                        <h4>{trI18n('MCP_SERVER_SETTING_ACTIVE_IN_ORG')}</h4>
                    </div>
                    <div className="mcp-server-last-title">
                        <h4>{trI18n('MCP_SERVER_SETTING_STATUS')}</h4>
                    </div>
                </div>}
                {!Utils.isEmptyArray(listSettingApi) && (
                    <div className="mcp-server-list" ref={mcpServerListBody}>
                        {listSettingApi.map(apiData => (
                            <div
                                key={apiData.id}
                                className="mcp-server-body-line mcp-server-line mcp-server-body-line-org"
                            >
                                <button
                                    className="mcp-server-item btn-reset-default"
                                    onClick={() => {
                                        handleOpenDetail(apiData);
                                    }}
                                >
                                    <OverFlowTooltipWithTabsMui
                                        value={Utils.serverNameWithPrefix(
                                            apiData.serverName,
                                            apiData.type
                                        )}
                                    />
                                </button>
                                {renderSettingMCPServer(
                                    apiData.isActiveCorp,
                                    callActiveInOrg,
                                    apiData,
                                    apiData.isActiveCorp,
                                    panelType
                                )}
                                {renderSettingApiStatus(apiData.status)}
                            </div>
                        ))}
                    </div>
                )}
                {showLocalMcpSection() && !Utils.isEmptyArray(localMcpList) && (
                    <>
                        <div className="mcp-server-body-line mcp-server-line-space mcp-server-body-line-org local-mcp-list">
                            <div className="">
                                <h4>{trI18n('LOCAL_MCP')}</h4>
                            </div>
                            <div className="mcp-server-last-title">
                                <h4>{trI18n('MCP_SERVER_SETTING_ACTIVE_IN_ORG')}</h4>
                            </div>
                            <div className="mcp-server-last-title">
                                <h4>{trI18n('MCP_SERVER_SETTING_STATUS')}</h4>
                            </div>
                        </div>
                        <div className="mcp-server-list">
                            {localMcpList.map(item => (
                                <div
                                    key={item.catId}
                                    className="mcp-server-body-line mcp-server-line mcp-server-body-line-org"
                                >
                                    <div className="mcp-server-item">
                                        <OverFlowTooltipWithTabsMui
                                            value={`${LOCAL_MCP_LABEL_PREFIX}${item.mcpName}`}
                                        />
                                    </div>
                                    {renderSettingMCPServer(
                                        item.isActive,
                                        value => callToggleLocalMcp(item.catId, value),
                                        {...item,id: item.catId},
                                        item.isActive,
                                        panelType
                                    )}
                                    <div className="mcp-server-status">
                                        <div className="mcp-server-item-status local-mcp-status">
                                            -
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
            <ConfirmDialog
                isOpen={isShowPopUpReload}
                msgConfirm={trI18n('COMMSG030')}
                okCallBack={() => window.location.reload()}
            />
        </div>
    );
}

export default MCPServerList;
